import { Suspense } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Loading } from "./States";

/**
 * The Settings area shell (Jonathan, 2026-10-22).
 *
 * Settings used to be three fly-out GROUPS in the sidebar, each with its own
 * sub-pages — "hard to find what you're looking for". It now works the way Clio
 * and Render do it: the sidebar "Settings" row is a plain link (nav.tsx,
 * `flyout: false`), and THIS layout draws a persistent vertical rail of every
 * settings page, FLAT, right after the main nav. The selected page renders to
 * its right. Three columns: main nav · settings rail · page.
 *
 * The rail is its own flat list rather than `SECTIONS`' Settings.tabs because it
 * carries the two Connections `?v=` cuts (Institutions, Held for review) as their
 * own rows and lights them by query string — a distinction the generic nav model
 * (pathname-only) does not make. Keep the two in step: a real page added to
 * Settings.tabs in nav.tsx should also appear here.
 *
 * Under 900px two rails do not fit, so `.settings-rail` becomes a horizontal
 * scroll strip above the page (styles.css) — the mobile top bar's Settings menu
 * still offers the same pages.
 */

interface RailPage {
  to: string;
  label: string;
  /** The `?v=` cut on `to`, for a page that carries several (Connections). */
  v?: "institutions" | "held";
  /** Extra pathnames that should keep this row lit (e.g. /readings → Connections). */
  match?: string[];
}

const PAGES: RailPage[] = [
  { to: "/floors", label: "Cash floors" },
  // The two email-recipient screens (Alert emails, Snapshot emails) moved INTO
  // the System page, stacked (Jonathan, 2026-11-03), to keep this rail short —
  // so they are no longer their own rows. /alerts and /snapshot-emails redirect
  // to System.
  // Institutions folded INTO the Connections page (Jonathan, 2026-11-03: "they
  // are both connections") — no longer its own row. Held for review stays its
  // own cut.
  { to: "/connections", label: "Connections" },
  { to: "/connections", label: "Held for review", v: "held" },
  // People + Activity are one entry now (Jonathan, 2026-11-03), two pages behind
  // an in-page switch; /activity lives under /people and lights this row.
  { to: "/people", label: "People / Activity", match: ["/activity"] },
  { to: "/feedback", label: "Feedback" },
  // Check printing moved INTO the System page (Jonathan, 2026-11-03), a section
  // like the email setups — no longer its own row. /settings/checks redirects.
  { to: "/settings/system", label: "System" },
  { to: "/settings/whats-new", label: "What's new" },
];

function onPath(page: RailPage, pathname: string): boolean {
  const paths = [page.to, ...(page.match ?? [])];
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function SettingsLayout() {
  const { pathname, search } = useLocation();

  // Which cut of the (only) multi-cut page is open; anything else is its default.
  const raw = new URLSearchParams(search).get("v");
  const cut: RailPage["v"] =
    raw === "institutions" || raw === "held" ? raw : undefined;

  return (
    <div className="settings-layout">
      <nav className="settings-rail no-print" aria-label="Settings">
        {/* The rail column is full height (so its divider runs all the way down);
            this inner list is what sticks under the deskbar and scrolls. */}
        <div className="settings-rail-inner">
          {PAGES.map((page) => {
            const to = page.v ? `${page.to}?v=${page.v}` : page.to;
            const active = onPath(page, pathname) && page.v === cut;
            return (
              <Link
                key={to + page.label}
                to={to}
                className={`settings-rail-item ${active ? "active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {page.label}
              </Link>
            );
          })}
        </div>
      </nav>
      <div className="settings-pane">
        {/* The page's own chunk is lazy (App.tsx); this boundary keeps the rail
            on screen while the pane loads, rather than blanking the whole area. */}
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}
